/**
 * 支付方式选择 + 支付流程 Modal
 *
 * 流程:
 * 1) 用户点"升级 Pro/MAX"
 * 2) 弹出本 Modal,显示支付方式选择
 * 3) 选择后调 /api/payment/create 创建订单
 * 4) 拿到 order_id + payment_url / qr_code
 * 5) 显示 QR(扫码)或跳转 URL,或 mock 时直接付款
 * 6) 轮询 /api/payment/status/:id 查结果
 * 7) paid → 调 onSuccess 关闭 Modal
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Smartphone, ScanLine, Zap, Crown, Loader, CheckCircle, AlertCircle, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../lib/api';

const TIERS = {
  pro_month: { name: 'Pro 月付', price: 169, desc: '30 天,所有功能' },
  pro_year:  { name: 'Pro 年付', price: 699, desc: '365 天,省 60%' },
  max:       { name: 'MAX 买断', price: 1999, desc: '永久使用,后续功能全送' },
};

// 支付方式列表 — 按当前真实配置显示
// 只保留可用的 alipay_h5
const METHODS = [
  { id: 'alipay_h5', name: '支付宝', icon: '💙', desc: '手机浏览器跳支付宝付款' },
];

export default function PaymentModal({ tier, onClose, onSuccess }) {
  const [step, setStep] = useState('select');  // select | pay | done | error
  const [method, setMethod] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const pollRef = useRef(null);

  const tierCfg = TIERS[tier];

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (order && order.expire_at) {
      const tick = () => {
        const left = Math.max(0, Math.floor((order.expire_at - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left <= 0 && step === 'pay') {
          setStep('error');
          setError('订单已过期,请重新下单');
          if (pollRef.current) clearInterval(pollRef.current);
        }
      };
      tick();
      const t = setInterval(tick, 1000);
      return () => clearInterval(t);
    }
  }, [order, step]);

  async function handleSelect(m) {
    setMethod(m);
    setLoading(true);
    setError(null);
    try {
      const r = await api.paymentCreate(tier, m.id);
      if (!r?.ok) {
        setError(r?.error || '下单失败');
        setStep('error');
        return;
      }
      if (r.direct_grant) {
        // 0 元档(试用),直接成功
        onSuccess && onSuccess(r);
        return;
      }
      setOrder(r);
      setStep('pay');
      // 开始轮询
      if (m.id !== 'mock') {
        startPoll(r.order_id);
      }
    } catch (e) {
      setError(e.message);
      setStep('error');
    } finally {
      setLoading(false);
    }
  }

  async function startPoll(orderId) {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.paymentStatus(orderId);
        if (s?.status === 'paid') {
          if (pollRef.current) clearInterval(pollRef.current);
          setStep('done');
          toast.success('🎉 支付成功!');
          setTimeout(() => {
            onSuccess && onSuccess(s);
          }, 1500);
        }
      } catch {}
    }, 3000);
  }

  async function handleMockPay() {
    if (!order) return;
    setLoading(true);
    try {
      const r = await api.paymentMockPay(order.order_id);
      if (r?.ok || r?.paid || r?.already_paid) {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep('done');
        toast.success('🎉 测试支付成功!');
        setTimeout(() => {
          onSuccess && onSuccess(r);
        }, 1500);
      } else {
        setError(r?.error || '支付失败');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function copyText(text, label) {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`✅ ${label} 已复制`);
    } catch {
      toast.error('复制失败');
    }
  }

  if (!tierCfg) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-bg-elevated rounded-2xl border border-white/10 shadow-2xl w-full max-w-md overflow-hidden">
        {/* 顶部 */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-brand-purple/20 to-brand-cyan/20">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-brand-gold" />
            <h2 className="text-lg font-bold">开通 {tierCfg.name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* 步骤指示 */}
        <div className="px-4 py-2 text-xs text-ink-3 flex items-center gap-2">
          <span className={step === 'select' ? 'text-brand-cyan' : 'text-ink-3'}>① 选支付方式</span>
          <span>→</span>
          <span className={step === 'pay' ? 'text-brand-cyan' : 'text-ink-3'}>② 支付</span>
          <span>→</span>
          <span className={step === 'done' ? 'text-brand-cyan' : 'text-ink-3'}>③ 完成</span>
        </div>

        <div className="p-4">
          {/* 商品信息 */}
          <div className="bg-white/5 rounded-xl p-3 mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-ink-3">订单商品</div>
              <div className="font-semibold">{tierCfg.name}</div>
              <div className="text-[10px] text-ink-3 mt-0.5">{tierCfg.desc}</div>
            </div>
            <div className="text-2xl font-bold text-brand-gold">¥{tierCfg.price}</div>
          </div>

          {/* Step 1: 选支付方式 */}
          {step === 'select' && (
            <div className="space-y-2">
              {METHODS.map(m => (
                <button
                  key={m.id}
                  onClick={() => handleSelect(m)}
                  disabled={loading}
                  className="w-full p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center gap-3 transition disabled:opacity-50"
                >
                  <div className="text-2xl">{m.icon}</div>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-[10px] text-ink-3">{m.desc}</div>
                  </div>
                  {loading && method?.id === m.id ? (
                    <Loader size={16} className="animate-spin text-brand-cyan" />
                  ) : (
                    <span className="text-ink-3 text-xs">选择 →</span>
                  )}
                </button>
              ))}
              {error && (
                <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 2: 支付 */}
          {step === 'pay' && order && (
            <div className="space-y-3">
              {/* Mock 模式直接点付款 */}
              {method?.id === 'mock' ? (
                <div className="space-y-3">
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-xs text-yellow-300">
                    🧪 <b>测试通道</b>:这是开发测试模式,点击下方按钮模拟支付成功
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-ink-3">订单号</span>
                      <span className="font-mono">{order.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-3">金额</span>
                      <span className="font-bold text-brand-gold">¥{order.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-3">商品</span>
                      <span>{order.tier_name}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleMockPay}
                    disabled={loading}
                    className="w-full py-3 bg-brand-gold text-black font-bold rounded-xl hover:bg-yellow-400 transition flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader size={16} className="animate-spin" /> : <Zap size={16} />}
                    模拟支付 ¥{order.amount}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* 二维码模式 */}
                  {order.qr_code ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="bg-white p-3 rounded-xl">
                        <QRPlaceholder data={order.qr_code} />
                      </div>
                      <div className="text-xs text-ink-3">
                        用 {method?.icon} {method?.name} 扫一扫
                      </div>
                    </div>
                  ) : (
                    /* H5 跳转模式 */
                    <a
                      href={order.payment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full py-3 bg-brand-cyan text-black font-bold rounded-xl hover:bg-cyan-400 transition flex items-center justify-center gap-2"
                    >
                      <ExternalLink size={16} />
                      前往{method?.name}支付
                    </a>
                  )}

                  <div className="bg-white/5 rounded-xl p-2 text-[10px] space-y-1">
                    <div className="flex justify-between">
                      <span className="text-ink-3">订单号</span>
                      <button onClick={() => copyText(order.order_id, '订单号')} className="font-mono flex items-center gap-1 hover:text-brand-cyan">
                        {order.order_id.slice(-10)}... <Copy size={9} />
                      </button>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-3">金额</span>
                      <span className="font-bold text-brand-gold">¥{order.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-3">剩余</span>
                      <span className={secondsLeft < 60 ? 'text-red-400' : ''}>
                        {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  <div className="text-[10px] text-ink-3 text-center">
                    支付完成后页面会自动跳转,无需刷新
                  </div>
                </div>
              )}

              <button
                onClick={() => { setStep('select'); setOrder(null); setError(null); if (pollRef.current) clearInterval(pollRef.current); }}
                className="w-full text-xs text-ink-3 hover:text-ink-1"
              >
                ← 重新选择支付方式
              </button>
            </div>
          )}

          {/* Step 3: 完成 */}
          {step === 'done' && (
            <div className="text-center py-6 space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20">
                <CheckCircle size={36} className="text-green-400" />
              </div>
              <div className="text-xl font-bold">支付成功!</div>
              <div className="text-sm text-ink-3">
                您的 <b className="text-brand-gold">{tierCfg.name}</b> 已开通,3 秒后自动跳转...
              </div>
            </div>
          )}

          {/* 错误 */}
          {step === 'error' && (
            <div className="text-center py-6 space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20">
                <AlertCircle size={36} className="text-red-400" />
              </div>
              <div className="text-sm text-red-400">{error || '出错了'}</div>
              <button
                onClick={() => { setStep('select'); setOrder(null); setError(null); if (pollRef.current) clearInterval(pollRef.current); }}
                className="px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
              >
                重试
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 二维码占位(不引第三方 lib,只显示 data + 提示)
// 真要二维码可以加 qrcode 库
function QRPlaceholder({ data }) {
  const isUrl = data && (data.startsWith('http') || data.startsWith('weixin') || data.startsWith('alipays'));
  return (
    <div className="w-48 h-48 flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border-2 border-dashed border-white/20 rounded-xl text-center p-3">
      <ScanLine size={48} className="text-brand-cyan mb-2" />
      <div className="text-[10px] text-ink-3 break-all">
        {isUrl ? data.slice(0, 30) + '...' : data.slice(0, 20) + '...'}
      </div>
      <div className="text-[9px] text-ink-3 mt-1">点击下方按钮跳转支付</div>
    </div>
  );
}
